import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Pagination as PageMeta } from "@taskiq/shared";
import { IconButton } from "./IconButton";

/**
 * The footer under a list: what you are looking at, and how to move.
 *
 * Every list in the console used to ask for `pageSize=100` and never send a
 * `page`, so the hundred-and-first row simply did not exist - no control, no
 * count, no indication. The API had returned `pagination` alongside the rows the
 * whole time and one screen read `total` off it, for a stat tile.
 *
 * ── Why the count is always shown and the buttons are not ───────────────────
 *
 * "Showing 1-25 of 240" is the part that answers the question the truncation
 * used to hide, so it renders whenever there is anything at all. The arrows only
 * appear when there is somewhere to go, because a permanently disabled control
 * is a promise the page cannot keep.
 *
 * Deliberately not a numbered page list. These are alphabetical operational
 * lists that people search rather than page through - "jump to page 7" is not a
 * thing anybody wants from a table of depots, and the numbers would be the
 * widest thing in the footer.
 */
export function Pagination({
  pagination,
  onPageChange,
  /** Plural noun for the summary - "sites", "people". Lowercase. */
  label,
}: {
  pagination: PageMeta;
  onPageChange: (page: number) => void;
  label: string;
}) {
  const { page, pageSize, total, totalPages } = pagination;
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  // The last page is short, so the range has to be clamped to the total rather
  // than computed from the page size - otherwise it reports rows that are not
  // on screen.
  const last = Math.min(page * pageSize, total);

  return (
    <nav className="pager" aria-label={`${label} pagination`}>
      <p className="pager__summary">
        {total <= pageSize ? (
          <>
            {total} {label}
          </>
        ) : (
          <>
            Showing {first}-{last} of {total} {label}
          </>
        )}
      </p>

      {totalPages > 1 && (
        <div className="pager__controls">
          {/* aria-live, so moving between pages is announced. Without it the
              table silently swaps its contents and a screen-reader user has no
              signal that the button did anything. */}
          <span className="pager__page" aria-live="polite">
            Page {page} of {totalPages}
          </span>
          <IconButton
            size="sm"
            label="Previous page"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            size="sm"
            label="Next page"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            <ChevronRight />
          </IconButton>
        </div>
      )}
    </nav>
  );
}
